import { Link, useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Users, Star, ArrowRight, PlayCircle } from 'lucide-react';
import styles from './course-card.module.css';
import { motion } from 'framer-motion';
import type { Course } from '../../data/mock-data';
import { API_ORIGIN } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useState } from 'react';
import BrochureModal from '../BrochureModal';
import EnrollPromptModal from '../EnrollPromptModal';

interface CourseCardProps {
  course: any;
  enrolled?: boolean;
  progress?: number;
  directTutorial?: boolean;
  layout?: 'grid' | 'list';
}

export function CourseCard({ course, enrolled = false, progress = 0, layout = 'grid' }: CourseCardProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showBrochure, setShowBrochure] = useState(false);
  const [showEnrollPrompt, setShowEnrollPrompt] = useState(false);
  const isList = layout === 'list';
  const normalizedProgress = Math.max(0, Math.min(100, Number(progress) || 0));
  const resolveUrl = (url?: string) => {
    if (!url) return 'https://placehold.co/600x400?text=Course';
    if (url.startsWith('http') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleDownloadBrochure = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (enrolled) {
      setShowBrochure(true);
    } else {
      setShowEnrollPrompt(true);
    }
  };

  const getNumericId = (id: any) => {
    if (!id) return '';
    const s = id.toString();
    if (s.startsWith('mod-')) return s.replace('mod-', '');
    if (s.startsWith('free-')) return s.replace('free-', '');
    return s;
  };

  const numericId = getNumericId(course.id);

  const courseLink = `/courses/${numericId}`;

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="h-100"
    >
      <Link to={courseLink} className={styles.card}>
        <div className={styles.thumbnail}>
          <img src={resolveUrl(course.thumbnail)} alt={course.title} loading="lazy" onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x400?text=Course'; }} />
          <div className={styles.category}>{course.category}</div>
        </div>
        <div className={styles.body}>
          <h3 className={styles.title}>{course.title}</h3>
          
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <Star size={14} className={styles.starIcon} fill="#FFBB00" />
              <span>{course.rating || 4.8}</span>
            </div>
            <div className={styles.metaItem}>
              <BookOpen size={14} />
              <span>{course.lessons || 12} Lessons</span>
            </div>
          </div>

          {(normalizedProgress > 0 || enrolled) && (
            <div className={styles.progressWrap}>
              <div className={styles.progressBar}>
                <div className={styles.progressFill} style={{ width: `${normalizedProgress}%` }} />
              </div>
              <span className={styles.progressText}>
                {normalizedProgress >= 100 ? 'Completed' : `${normalizedProgress}% complete`}
              </span>
            </div>
          )}

          {!enrolled && (course.regular_price !== undefined || course.sale_price !== undefined) && (
            <div className={styles.priceWrapper}>
              <div>
                <span className={styles.price}>₹{course.sale_price || course.regular_price}</span>
                {course.sale_price && course.regular_price && (
                  <span className={styles.oldPrice}>₹{course.regular_price}</span>
                )}
              </div>
              <ArrowRight size={18} color="#7C6EFF" />
            </div>
          )}
        </div>
      </Link>
      <BrochureModal
        show={showBrochure}
        onHide={() => setShowBrochure(false)}
        brochureUrl={course.brochure_url ? resolveUrl(course.brochure_url) : null}
        courseTitle={course.title}
      />
      <EnrollPromptModal
        show={showEnrollPrompt}
        onHide={() => setShowEnrollPrompt(false)}
        onEnroll={() => {
          if (!user) {
            navigate(location.pathname, { state: { openLogin: true, authMode: 'signup' } });
          } else {
            navigate(courseLink);
          }
        }}
        courseTitle={course.title}
      />
    </motion.div>
  );
}
